import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Pencil, Bell, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Save, Wallet, X } from 'lucide-react';
import { AccountingLayout } from '../components/AccountingLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  listReminders, createReminder, updateReminder, deleteReminder, toggleReminderPaid, moveReminder,
  listPaymentNotes, createPaymentNote, updatePaymentNote, deletePaymentNote,
} from '../lib/api';
import { formatRp, parseNumber, formatThousands } from '../lib/format';

const MONTHS_ID = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember',
];
const DAYS_ID = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

const CATEGORIES = [
  { value: 'cicilan',  label: 'Cicilan',  color: 'bg-rose-100 text-rose-700 border-rose-200',       dot: 'bg-rose-500' },
  { value: 'listrik',  label: 'Listrik',  color: 'bg-amber-100 text-amber-700 border-amber-200',    dot: 'bg-amber-500' },
  { value: 'gaji',     label: 'Gaji',     color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  { value: 'air',      label: 'Air',      color: 'bg-sky-100 text-sky-700 border-sky-200',          dot: 'bg-sky-500' },
  { value: 'internet', label: 'Internet', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', dot: 'bg-indigo-500' },
  { value: 'sewa',     label: 'Sewa',     color: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  { value: 'pajak',    label: 'Pajak',    color: 'bg-slate-100 text-slate-700 border-slate-200',    dot: 'bg-slate-500' },
  { value: 'lainnya',  label: 'Lainnya',  color: 'bg-stone-100 text-stone-700 border-stone-200',    dot: 'bg-stone-400' },
];

const catMeta = (v) => CATEGORIES.find((c) => c.value === v) || CATEGORIES[CATEGORIES.length - 1];

const monthKey = (y, m) => `${y}-${String(m + 1).padStart(2, '0')}`;
const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const emptyForm = () => ({ name: '', category: 'lainnya', amount: '', day_of_month: 1, notes: '' });

export default function RemindersPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-11
  const [reminders, setReminders] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // Payment notes (independent from reminders)
  const emptyNote = () => ({ note_date: '', description: '', amount: '', status: 'unpaid' });
  const [paymentNotes, setPaymentNotes] = useState([]);
  const [noteOpen, setNoteOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteForm, setNoteForm] = useState(emptyNote());
  const [savingNote, setSavingNote] = useState(false);
  const [selectedNoteIds, setSelectedNoteIds] = useState(new Set());

  const refresh = async () => {
    try {
      const data = await listReminders();
      setReminders(data);
    } catch {
      toast.error('Gagal memuat pengingat');
    }
  };

  const refreshNotes = async () => {
    try {
      const data = await listPaymentNotes(year, month + 1);
      setPaymentNotes(Array.isArray(data) ? data : []);
      setSelectedNoteIds(new Set()); // reset selection when month changes
    } catch { /* silent */ }
  };
  useEffect(() => { refreshNotes(); /* eslint-disable-next-line */ }, [year, month]);

  useEffect(() => { refresh(); }, []);

  const key = monthKey(year, month);
  const daysCount = daysInMonth(year, month);
  const firstDow = new Date(year, month, 1).getDay(); // 0-6 (Sun-Sat)

  // Reminders effective this month: day_of_month must exist in this month
  const monthReminders = useMemo(
    () => reminders.filter((r) => r.is_active !== false && r.day_of_month <= daysCount),
    [reminders, daysCount]
  );

  // Sorted for sidebar list (urutan manual per tanggal via sort_order)
  const sortedForList = useMemo(
    () => [...monthReminders].sort((a, b) =>
      a.day_of_month - b.day_of_month
      || (a.sort_order ?? 0) - (b.sort_order ?? 0)
      || a.name.localeCompare(b.name)),
    [monthReminders]
  );

  // Posisi tiap pengingat dalam tanggalnya (batas atas/bawah tombol urutan)
  const dayPosReminder = useMemo(() => {
    const byDate = {};
    for (const r of sortedForList) (byDate[r.day_of_month] = byDate[r.day_of_month] || []).push(r.id);
    const pos = {};
    for (const ids of Object.values(byDate)) {
      ids.forEach((id, i) => { pos[id] = { first: i === 0, last: i === ids.length - 1 }; });
    }
    return pos;
  }, [sortedForList]);

  const moveReminderHandler = async (r, direction) => {
    try {
      await moveReminder(r.id, direction);
      await refresh();
    } catch { toast.error('Gagal mengubah urutan'); }
  };

  // Group by day (1..daysCount), urut sesuai sort_order agar kalender mengikuti daftar
  const byDay = useMemo(() => {
    const m = {};
    for (const r of sortedForList) {
      (m[r.day_of_month] = m[r.day_of_month] || []).push(r);
    }
    return m;
  }, [sortedForList]);

  const total = monthReminders.reduce((s, r) => s + Number(r.amount || 0), 0);
  const paidTotal = monthReminders
    .filter((r) => (r.paid_months || []).includes(key))
    .reduce((s, r) => s + Number(r.amount || 0), 0);
  const unpaidCount = monthReminders.filter((r) => !(r.paid_months || []).includes(key)).length;

  // Grid: 6 weeks × 7 days
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysCount; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  while (cells.length < 42) cells.push(null);

  const todayD = (year === now.getFullYear() && month === now.getMonth()) ? now.getDate() : -1;

  const goPrev = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1);
  };
  const goNext = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1);
  };
  const goToday = () => { setYear(now.getFullYear()); setMonth(now.getMonth()); };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      name: r.name || '',
      category: r.category || 'lainnya',
      amount: r.amount ? formatThousands(String(r.amount)) : '',
      day_of_month: r.day_of_month || 1,
      notes: r.notes || '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Nama wajib diisi');
    const day = Number(form.day_of_month);
    if (!day || day < 1 || day > 31) return toast.error('Tanggal 1-31');
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        amount: parseNumber(form.amount) || 0,
        day_of_month: day,
        notes: form.notes.trim(),
      };
      if (editingId) {
        await updateReminder(editingId, payload);
        toast.success('Pengingat diperbarui');
      } else {
        await createReminder(payload);
        toast.success('Pengingat ditambahkan');
      }
      setOpen(false);
      setEditingId(null);
      setForm(emptyForm());
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Hapus pengingat "${r.name}"?`)) return;
    try {
      await deleteReminder(r.id);
      toast.success('Pengingat dihapus');
      await refresh();
    } catch {
      toast.error('Gagal menghapus');
    }
  };

  const togglePaid = async (r) => {
    try {
      const updated = await toggleReminderPaid(r.id, key);
      setReminders((arr) => arr.map((x) => (x.id === r.id ? updated : x)));
    } catch {
      toast.error('Gagal mengubah status');
    }
  };

  // Payment notes handlers
  const notesByDay = useMemo(() => {
    const m = {};
    for (const n of paymentNotes) {
      const day = Number((n.note_date || '').split('-')[2]);
      if (day) (m[day] = m[day] || []).push(n);
    }
    return m;
  }, [paymentNotes]);

  const notesTotal = paymentNotes.reduce((s, n) => s + Number(n.amount || 0), 0);
  const notesPaid = paymentNotes.filter((n) => n.status === 'paid').reduce((s, n) => s + Number(n.amount || 0), 0);
  const notesUnpaid = paymentNotes.filter((n) => n.status === 'unpaid').reduce((s, n) => s + Number(n.amount || 0), 0);

  // Selection helpers
  const sortedNotes = useMemo(
    () => [...paymentNotes].sort((a, b) => a.note_date.localeCompare(b.note_date)),
    [paymentNotes]
  );
  const toggleNoteSelected = (id) => setSelectedNoteIds((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAllNotes = () => setSelectedNoteIds((s) => {
    if (s.size === paymentNotes.length) return new Set();
    return new Set(paymentNotes.map((n) => n.id));
  });
  const selectedNotes = paymentNotes.filter((n) => selectedNoteIds.has(n.id));
  const selectedTotal = selectedNotes.reduce((s, n) => s + Number(n.amount || 0), 0);
  const selectedPaid = selectedNotes.filter((n) => n.status === 'paid').reduce((s, n) => s + Number(n.amount || 0), 0);
  const selectedUnpaid = selectedNotes.filter((n) => n.status === 'unpaid').reduce((s, n) => s + Number(n.amount || 0), 0);

  const openCreateNote = () => {
    setEditingNoteId(null);
    setNoteForm({ ...emptyNote(), note_date: `${year}-${String(month + 1).padStart(2, '0')}-01` });
    setNoteOpen(true);
  };
  const openEditNote = (n) => {
    setEditingNoteId(n.id);
    setNoteForm({
      note_date: n.note_date || '',
      description: n.description || '',
      amount: n.amount ? formatThousands(String(n.amount)) : '',
      status: n.status || 'unpaid',
    });
    setNoteOpen(true);
  };
  const saveNote = async () => {
    if (!noteForm.description.trim()) return toast.error('Keterangan wajib diisi');
    if (!noteForm.note_date) return toast.error('Tanggal wajib diisi');
    setSavingNote(true);
    try {
      const payload = {
        note_date: noteForm.note_date,
        description: noteForm.description.trim(),
        amount: parseNumber(noteForm.amount) || 0,
        status: noteForm.status === 'paid' ? 'paid' : 'unpaid',
      };
      if (editingNoteId) await updatePaymentNote(editingNoteId, payload);
      else await createPaymentNote(payload);
      toast.success(editingNoteId ? 'Catatan diperbarui' : 'Catatan ditambahkan');
      setNoteOpen(false); setEditingNoteId(null); setNoteForm(emptyNote());
      await refreshNotes();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal menyimpan'); }
    finally { setSavingNote(false); }
  };
  const removeNote = async (n) => {
    if (!window.confirm(`Hapus catatan "${n.description}"?`)) return;
    try { await deletePaymentNote(n.id); toast.success('Catatan dihapus'); await refreshNotes(); }
    catch { toast.error('Gagal menghapus'); }
  };
  const toggleNoteStatus = async (n) => {
    try {
      const updated = await updatePaymentNote(n.id, {
        note_date: n.note_date, description: n.description, amount: n.amount,
        status: n.status === 'paid' ? 'unpaid' : 'paid',
      });
      setPaymentNotes((arr) => arr.map((x) => (x.id === n.id ? updated : x)));
    } catch { toast.error('Gagal mengubah status'); }
  };

  return (
    <AccountingLayout>
      <div className="space-y-6" data-testid="reminders-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">
              Kalender &amp; Pengingat
            </h1>
            <p className="text-sm text-stone-500 mt-2 max-w-xl">
              Jadwal pembayaran bulanan rutin: gaji, listrik, cicilan, dan lainnya
            </p>
          </div>
          <Button
            onClick={openCreate}
            data-testid="btn-add-reminder"
            className="bg-emerald-800 hover:bg-emerald-900 text-white h-11 px-5 rounded-lg gap-2"
          >
            <Plus className="w-4 h-4" />
            Tambah Pengingat
          </Button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-xl border border-stone-200 bg-white px-6 py-5" data-testid="stat-total">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">
              Total Tagihan Bulan Ini
            </div>
            <div className="text-3xl font-semibold text-stone-900 mt-2 font-mono">
              {formatRp(total)}
            </div>
          </div>
          <div className="rounded-xl border border-stone-200 bg-white px-6 py-5" data-testid="stat-paid">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">
              Sudah Dibayar
            </div>
            <div className="text-3xl font-semibold text-emerald-700 mt-2 font-mono">
              {formatRp(paidTotal)}
            </div>
          </div>
          <div className="rounded-xl border border-stone-200 bg-white px-6 py-5" data-testid="stat-unpaid">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">
              Belum Dibayar
            </div>
            <div className="text-3xl font-semibold text-rose-600 mt-2 font-mono">
              {unpaidCount} <span className="text-lg font-normal">tagihan</span>
            </div>
          </div>
        </div>

        {/* Body: calendar + list */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,340px] gap-6">
          {/* Calendar */}
          <div className="rounded-xl border border-stone-200 bg-white p-5" data-testid="calendar-card">
            <div className="flex items-center justify-between mb-5">
              <div className="text-lg font-semibold text-stone-900">
                {MONTHS_ID[month]} {year}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={goPrev}
                  data-testid="btn-prev-month"
                  className="h-9 w-9 border-stone-200"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  onClick={goToday}
                  data-testid="btn-today"
                  className="h-9 px-4 border-stone-200 text-sm"
                >
                  Hari Ini
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={goNext}
                  data-testid="btn-next-month"
                  className="h-9 w-9 border-stone-200"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-px bg-stone-200 border border-stone-200 rounded-md overflow-hidden">
              {DAYS_ID.map((d) => (
                <div key={d} className="bg-white text-xs text-stone-500 font-medium py-2 text-center">
                  {d}
                </div>
              ))}
              {cells.map((d, idx) => {
                const items = d ? (byDay[d] || []) : [];
                const isToday = d === todayD;
                return (
                  <div
                    key={idx}
                    data-testid={d ? `cal-day-${d}` : undefined}
                    className={`bg-white min-h-[92px] p-2 relative ${
                      isToday ? 'ring-2 ring-emerald-500 ring-inset bg-emerald-50/40' : ''
                    }`}
                  >
                    {d && (
                      <>
                        <div className={`text-sm ${isToday ? 'text-emerald-800 font-semibold' : 'text-stone-700'}`}>
                          {d}
                        </div>
                        <div className="mt-1 space-y-0.5">
                          {items.slice(0, 3).map((r) => {
                            const meta = catMeta(r.category);
                            const paid = (r.paid_months || []).includes(key);
                            return (
                              <div
                                key={r.id}
                                onClick={() => openEdit(r)}
                                className={`text-[10px] leading-tight truncate flex items-center gap-1 cursor-pointer hover:opacity-70 ${
                                  paid ? 'line-through text-stone-400' : 'text-stone-700'
                                }`}
                                title={`${r.name} · ${formatRp(r.amount)}`}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot} shrink-0`} />
                                <span className="truncate">{r.name}</span>
                              </div>
                            );
                          })}
                          {items.length > 3 && (
                            <div className="text-[10px] text-stone-400">+{items.length - 3} lagi</div>
                          )}
                        </div>
                        {/* Payment note dots */}
                        {(notesByDay[d] || []).length > 0 && (
                          <div className="absolute bottom-1 right-1 flex gap-0.5" title={`${(notesByDay[d] || []).length} catatan pembayaran`}>
                            {(notesByDay[d] || []).slice(0, 3).map((n) => (
                              <span
                                key={n.id}
                                className={`w-1.5 h-1.5 rounded-full ${n.status === 'paid' ? 'bg-emerald-500' : 'bg-rose-500'}`}
                              />
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Sidebar list */}
          <div className="rounded-xl border border-stone-200 bg-white p-5" data-testid="reminders-list">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-4 h-4 text-emerald-800" />
              <div className="text-sm font-semibold text-stone-900">Daftar Pengingat</div>
            </div>
            {sortedForList.length === 0 ? (
              <div className="text-sm text-stone-500 py-8 text-center">
                Belum ada pengingat. Klik <b>Tambah Pengingat</b>.
              </div>
            ) : (
              <div className="space-y-2 max-h-[560px] overflow-auto pr-1">
                {sortedForList.map((r) => {
                  const meta = catMeta(r.category);
                  const paid = (r.paid_months || []).includes(key);
                  return (
                    <div
                      key={r.id}
                      data-testid={`reminder-item-${r.id}`}
                      className="flex items-start gap-3 rounded-lg border border-stone-200 p-3 hover:border-emerald-300 transition-colors"
                    >
                      <div className="text-center shrink-0 w-9">
                        <div className="text-[10px] text-stone-500 uppercase">Tgl</div>
                        <div className="text-lg font-semibold text-stone-900 leading-none">
                          {r.day_of_month}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-medium text-stone-900 truncate">{r.name}</div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${meta.color}`}>
                            {meta.label}
                          </span>
                        </div>
                        <div className="text-sm text-stone-700 font-mono mt-0.5">
                          {formatRp(r.amount)}
                        </div>
                        <button
                          onClick={() => togglePaid(r)}
                          data-testid={`toggle-paid-${r.id}`}
                          className={`inline-flex items-center gap-1 text-xs mt-1.5 rounded px-1.5 py-0.5 transition-colors ${
                            paid
                              ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                              : 'text-stone-500 bg-stone-100 hover:bg-stone-200'
                          }`}
                        >
                          {paid ? '✓ Sudah dibayar' : '○ Belum dibayar'}
                        </button>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <button
                          onClick={() => moveReminderHandler(r, 'up')}
                          disabled={dayPosReminder[r.id]?.first}
                          data-testid={`move-up-reminder-${r.id}`}
                          className="p-1.5 rounded hover:bg-stone-100 text-stone-500 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          title="Naikkan urutan"
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => moveReminderHandler(r, 'down')}
                          disabled={dayPosReminder[r.id]?.last}
                          data-testid={`move-down-reminder-${r.id}`}
                          className="p-1.5 rounded hover:bg-stone-100 text-stone-500 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          title="Turunkan urutan"
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => openEdit(r)}
                          data-testid={`edit-reminder-${r.id}`}
                          className="p-1.5 rounded hover:bg-stone-100 text-stone-500"
                          title="Edit"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => remove(r)}
                          data-testid={`delete-reminder-${r.id}`}
                          className="p-1.5 rounded hover:bg-rose-50 text-rose-500"
                          title="Hapus"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Dialog */}
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(emptyForm()); } }}>
          <DialogContent className="sm:max-w-md" data-testid="reminder-dialog">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Pengingat' : 'Tambah Pengingat'}</DialogTitle>
              <DialogDescription>
                Jadwal pembayaran bulanan rutin (setiap tanggal yang sama tiap bulan).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Nama</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="misal: Listrik PLN"
                  data-testid="input-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Kategori</Label>
                  <Select
                    value={form.category}
                    onValueChange={(v) => setForm({ ...form, category: v })}
                  >
                    <SelectTrigger data-testid="select-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Tanggal Jatuh Tempo (1-31)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={31}
                    value={form.day_of_month}
                    onChange={(e) => setForm({ ...form, day_of_month: e.target.value })}
                    data-testid="input-day"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Jumlah (Rp)</Label>
                <Input
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: formatThousands(e.target.value) })}
                  placeholder="0"
                  inputMode="numeric"
                  data-testid="input-amount"
                />
              </div>
              <div>
                <Label className="text-xs">Catatan (opsional)</Label>
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="input-notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                data-testid="btn-cancel"
              >
                Batal
              </Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-emerald-800 hover:bg-emerald-900 text-white gap-2"
                data-testid="btn-save"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Menyimpan…' : 'Simpan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ============ CATATAN PEMBAYARAN ============ */}
        <div className="rounded-xl border border-stone-200 bg-white p-5" data-testid="payment-notes-card">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Wallet className="w-4 h-4 text-emerald-800" />
              <div>
                <div className="text-base font-semibold text-stone-900">Catatan Pembayaran</div>
                <div className="text-xs text-stone-500">Bulan {MONTHS_ID[month]} {year} · {paymentNotes.length} catatan</div>
              </div>
            </div>
            <Button
              onClick={openCreateNote}
              data-testid="btn-add-note"
              className="bg-emerald-800 hover:bg-emerald-900 text-white h-9 px-4 rounded-lg gap-2 text-sm"
            >
              <Plus className="w-4 h-4" /> Tambah Catatan
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="rounded-lg border border-stone-200 px-4 py-3" data-testid="note-stat-total">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Total Bulan Ini</div>
              <div className="text-xl font-semibold text-stone-900 mt-1 font-mono">{formatRp(notesTotal)}</div>
            </div>
            <div className="rounded-lg border border-rose-200 bg-rose-50/40 px-4 py-3" data-testid="note-stat-unpaid">
              <div className="text-[10px] uppercase tracking-widest text-rose-700 font-semibold">Belum Dibayar</div>
              <div className="text-xl font-semibold text-rose-700 mt-1 font-mono">{formatRp(notesUnpaid)}</div>
            </div>
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 px-4 py-3" data-testid="note-stat-paid">
              <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">Sudah Dibayar</div>
              <div className="text-xl font-semibold text-emerald-700 mt-1 font-mono">{formatRp(notesPaid)}</div>
            </div>
          </div>

          <div className="rounded-lg border border-stone-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                <tr>
                  <th className="px-3 py-2 text-center w-[40px]">
                    <input
                      type="checkbox"
                      checked={paymentNotes.length > 0 && selectedNoteIds.size === paymentNotes.length}
                      onChange={toggleAllNotes}
                      className="w-4 h-4 rounded border-stone-300 text-emerald-700 focus:ring-emerald-500 cursor-pointer"
                      data-testid="note-check-all"
                      aria-label="Pilih semua"
                    />
                  </th>
                  <th className="px-3 py-2 text-left w-[130px]">Tanggal</th>
                  <th className="px-3 py-2 text-left">Keterangan</th>
                  <th className="px-3 py-2 text-right w-[160px]">Nominal</th>
                  <th className="px-3 py-2 text-left w-[130px]">Status</th>
                  <th className="px-3 py-2 text-right w-[110px]">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {paymentNotes.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-stone-400 py-8 text-sm">
                    Belum ada catatan bulan ini. Klik "Tambah Catatan".
                  </td></tr>
                )}
                {sortedNotes.map((n) => {
                  const checked = selectedNoteIds.has(n.id);
                  return (
                  <tr key={n.id} className={`border-t border-stone-100 ${checked ? 'bg-emerald-50/40' : ''}`} data-testid={`note-row-${n.id}`}>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleNoteSelected(n.id)}
                        className="w-4 h-4 rounded border-stone-300 text-emerald-700 focus:ring-emerald-500 cursor-pointer"
                        data-testid={`note-check-${n.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-stone-700">{n.note_date}</td>
                    <td className="px-3 py-2 text-stone-800">{n.description}</td>
                    <td className="px-3 py-2 text-right font-mono text-stone-900">{formatRp(n.amount)}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => toggleNoteStatus(n)}
                        className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full border transition ${
                          n.status === 'paid'
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100'
                            : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                        }`}
                        data-testid={`note-status-${n.id}`}>
                        {n.status === 'paid' ? '✓ Sudah Dibayar' : '○ Belum Dibayar'}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => openEditNote(n)}
                        className="p-1.5 rounded hover:bg-stone-100 text-stone-500"
                        data-testid={`note-edit-${n.id}`} title="Edit">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => removeNote(n)}
                        className="p-1.5 rounded hover:bg-rose-50 text-rose-500 ml-1"
                        data-testid={`note-del-${n.id}`} title="Hapus">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>

          {/* Selection summary bar */}
          {selectedNoteIds.size > 0 && (
            <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="note-selection-bar">
              <div className="flex items-center gap-4 text-sm flex-wrap">
                <span className="font-semibold text-emerald-900">
                  {selectedNoteIds.size} dipilih
                </span>
                <span className="text-stone-600">
                  Total: <span className="font-mono font-semibold text-stone-900" data-testid="note-selected-total">{formatRp(selectedTotal)}</span>
                </span>
                <span className="text-emerald-800">
                  Sudah Dibayar: <span className="font-mono font-semibold" data-testid="note-selected-paid">{formatRp(selectedPaid)}</span>
                </span>
                <span className="text-rose-700">
                  Belum Dibayar: <span className="font-mono font-semibold" data-testid="note-selected-unpaid">{formatRp(selectedUnpaid)}</span>
                </span>
              </div>
              <button
                onClick={() => setSelectedNoteIds(new Set())}
                className="text-xs font-medium text-emerald-800 hover:text-emerald-900 px-2 py-1 rounded hover:bg-emerald-100 transition inline-flex items-center gap-1"
                data-testid="note-selection-clear"
              >
                <X className="w-3 h-3" /> Batal Pilih
              </button>
            </div>
          )}
        </div>

        {/* Payment note dialog */}
        <Dialog open={noteOpen} onOpenChange={(v) => { setNoteOpen(v); if (!v) { setEditingNoteId(null); setNoteForm(emptyNote()); } }}>
          <DialogContent className="sm:max-w-md" data-testid="note-dialog">
            <DialogHeader>
              <DialogTitle>{editingNoteId ? 'Edit Catatan' : 'Tambah Catatan Pembayaran'}</DialogTitle>
              <DialogDescription>
                Catatan tersimpan permanen dan tampil di bulan yang sesuai dengan tanggalnya.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Tanggal</Label>
                <Input type="date" value={noteForm.note_date}
                  onChange={(e) => setNoteForm({ ...noteForm, note_date: e.target.value })}
                  data-testid="note-input-date" />
              </div>
              <div>
                <Label className="text-xs">Keterangan</Label>
                <Input value={noteForm.description}
                  onChange={(e) => setNoteForm({ ...noteForm, description: e.target.value })}
                  placeholder="misal: BCA MAMI"
                  data-testid="note-input-desc" />
              </div>
              <div>
                <Label className="text-xs">Nominal (Rp)</Label>
                <Input value={noteForm.amount}
                  onChange={(e) => setNoteForm({ ...noteForm, amount: formatThousands(e.target.value) })}
                  placeholder="0" inputMode="numeric"
                  data-testid="note-input-amount" />
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={noteForm.status} onValueChange={(v) => setNoteForm({ ...noteForm, status: v })}>
                  <SelectTrigger data-testid="note-select-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">Belum Dibayar</SelectItem>
                    <SelectItem value="paid">Sudah Dibayar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNoteOpen(false)}>Batal</Button>
              <Button onClick={saveNote} disabled={savingNote}
                className="bg-emerald-800 hover:bg-emerald-900 text-white gap-2"
                data-testid="note-btn-save">
                <Save className="w-4 h-4" />
                {savingNote ? 'Menyimpan…' : 'Simpan'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AccountingLayout>
  );
}
